/**
 * Pay by bank (ACH through Stripe) on one invoice: OPEN-212 PR B (DEC-387).
 *
 *   POST /invoices/:id/pay-session          a Stripe Checkout link for the open balance
 *   GET  /invoices/:id/payment-attempts     every try, newest first
 *   POST /payment-attempts/:id/cancel       close a link nobody has used yet
 *
 * Paelode staff make a link and send it to the customer (the email link and the customer's
 * billing page come in PR C). A submitted debit shows as "Settling" until Stripe reports it: the
 * payment then appears in Payments above, recorded automatically. A bank typed in by hand first
 * waits for the customer to confirm Stripe's micro-deposits: "Bank check" with Stripe's page
 * (OPEN-223). Test mode only for now.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { Pill, subText } from '../../ui/bits';
import {
  useInvoiceMutation,
  usePaymentAttempts,
  type Invoice,
  type PaymentAttempt,
  type PaymentAttemptStatus,
  type PaySession,
} from './billing-hooks';
import { billingErrorText } from './billing-format';
import { money } from './invoice-format';

const cell: React.CSSProperties = { padding: '4px 6px' };

export const ATTEMPT_LABELS: Readonly<
  Record<PaymentAttemptStatus, { label: string; tone: 'muted' | 'warning' | 'positive' | 'danger' }>
> = {
  OPEN: { label: 'Link open', tone: 'muted' },
  PENDING: { label: 'Settling', tone: 'warning' },
  SUCCEEDED: { label: 'Paid', tone: 'positive' },
  FAILED: { label: 'Failed', tone: 'danger' },
  CANCELED: { label: 'Closed', tone: 'muted' },
};

/** The status pill: a PENDING debit still waiting on micro-deposits reads "Bank check". */
export function attemptLabel(a: Pick<PaymentAttempt, 'status' | 'verifyUrl'>): {
  label: string;
  tone: 'muted' | 'warning' | 'positive' | 'danger';
} {
  if (a.status === 'PENDING' && a.verifyUrl !== null)
    return { label: 'Bank check', tone: 'warning' };
  return ATTEMPT_LABELS[a.status];
}

function copy(url: string): void {
  void navigator.clipboard
    .writeText(url)
    .then(() => toast.success('Pay link copied.'))
    .catch(() => toast.error('Could not copy. Select the link and copy it by hand.'));
}

function CancelLink({ a }: { a: PaymentAttempt }): React.JSX.Element {
  const cancel = useInvoiceMutation<Record<string, never>, { canceled: boolean }>(
    `/payment-attempts/${a.id}/cancel`,
    'POST',
    {
      onSuccess: () => toast.success('Pay link closed. It can no longer be paid.'),
      onError: (err) => toast.error(billingErrorText(err, 'Could not close the link.')),
    },
  );
  return (
    <Button
      type="button"
      variant="outline"
      disabled={cancel.isPending}
      onClick={(): void => cancel.mutate({})}
    >
      Close link
    </Button>
  );
}

export function InvoiceOnlinePay({ inv }: { inv: Invoice }): React.JSX.Element {
  const { data } = usePaymentAttempts(inv.id);
  const attempts = data?.attempts ?? [];
  const [made, setMade] = React.useState<PaySession | null>(null);
  const create = useInvoiceMutation<Record<string, never>, { paySession: PaySession }>(
    `/invoices/${inv.id}/pay-session`,
    'POST',
    {
      onSuccess: () => undefined,
      onError: (err) => toast.error(billingErrorText(err, 'Could not make a pay link.')),
    },
  );
  const live = attempts.find((a) => a.status === 'OPEN' || a.status === 'PENDING');
  const canMake = inv.status === 'SENT' && live?.status !== 'PENDING';

  return (
    <section data-testid="invoice-online-pay" style={{ marginTop: '14px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 6px' }}>
        Pay by bank (Stripe, test mode)
      </h3>
      {canMake && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            type="button"
            disabled={create.isPending}
            onClick={(): void => create.mutate({}, { onSuccess: (res) => setMade(res.paySession) })}
          >
            {live?.status === 'OPEN' ? 'Show pay link' : 'Make pay link'}
          </Button>
          {made !== null && (
            <>
              <code data-testid="pay-link" style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                {made.url}
              </code>
              <Button type="button" variant="outline" onClick={(): void => copy(made.url)}>
                Copy
              </Button>
              <span style={subText}>
                For {money(made.amountCents)}, until {made.expiresAt.slice(0, 16).replace('T', ' ')}{' '}
                UTC.
              </span>
            </>
          )}
        </div>
      )}
      {live?.status === 'PENDING' && live.verifyUrl !== null && (
        <p style={{ ...subText, marginTop: 0 }} data-testid="pay-verifying">
          Waiting for the customer to confirm their bank. Stripe sent two small deposits or a code
          to their account; they enter it on{' '}
          <a href={live.verifyUrl} target="_blank" rel="noreferrer">
            Stripe&apos;s verification page
          </a>
          . The {money(live.amountCents)} debit starts after that.
        </p>
      )}
      {live?.status === 'PENDING' && live.verifyUrl === null && (
        <p style={{ ...subText, marginTop: 0 }} data-testid="pay-settling">
          {money(live.amountCents)} is settling from the customer&apos;s bank (usually 4 business
          days). It is recorded in Payments when it settles.
        </p>
      )}
      {attempts.length > 0 && (
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '6px' }}
        >
          <thead>
            <tr style={{ textAlign: 'left', ...subText }}>
              <th style={cell}>Started</th>
              <th style={cell}>Status</th>
              <th style={{ ...cell, textAlign: 'right' }}>Amount</th>
              <th style={cell}>Detail</th>
              <th style={cell} />
            </tr>
          </thead>
          <tbody>
            {attempts.map((a) => (
              <tr
                key={a.id}
                data-testid={`attempt-${a.id}`}
                style={{ borderTop: '1px solid var(--color-border-primary)' }}
              >
                <td style={cell}>{a.createdAt.slice(0, 10)}</td>
                <td style={cell}>
                  <Pill tone={attemptLabel(a).tone} label={attemptLabel(a).label} />
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>{money(a.amountCents)}</td>
                <td style={{ ...cell, ...subText }}>
                  {a.status === 'FAILED'
                    ? (a.failureMessage ?? a.failureCode ?? 'failed')
                    : a.status === 'CANCELED'
                      ? (a.failureMessage ?? '')
                      : ''}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {a.status === 'OPEN' && <CancelLink a={a} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
